import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import FilterSidebar from './components/FilterSidebar';
import ConsultationTable from './components/ConsultationTable';
import BulkOperationsToolbar from './components/BulkOperationsToolbar';
import PaginationControls from './components/PaginationControls';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  ConsultationHistoryItem,
  FilterState,
  SavedFilter,
  SortConfig,
  ColumnConfig,
  PaginationState
} from './types';
import { exportToExcel, exportToCSV } from '../../utils/exportUtils';

const ConsultationHistory = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    dateRange: { start: null, end: null },
    status: [],
    companyName: '',
    ufOrigin: [],
    ufDestination: [],
    taxRegime: [],
    apiDataFreshness: [],
    minTaxAmount: '',
    maxTaxAmount: ''
  });

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([
    {
      id: '1',
      name: 'Consultas SP 2024',
      filters: {
        dateRange: { start: new Date('2024-01-01'), end: new Date('2024-12-31') },
        status: ['success'],
        companyName: '',
        ufOrigin: ['SP'],
        ufDestination: [],
        taxRegime: [],
        apiDataFreshness: [],
        minTaxAmount: '',
        maxTaxAmount: ''
      },
      createdDate: new Date('2024-01-15')
    }
  ]);

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'createdDate',
    direction: 'desc'
  });

  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { id: 'consultationId', label: 'ID Consulta', visible: true },
    { id: 'companyName', label: 'Empresa', visible: true },
    { id: 'createdDate', label: 'Data Criação', visible: true },
    { id: 'status', label: 'Status', visible: true },
    { id: 'productCount', label: 'Produtos', visible: true },
    { id: 'totalTaxAmount', label: 'Total Impostos', visible: true },
    { id: 'ufOrigin', label: 'UF Origem', visible: false },
    { id: 'ufDestination', label: 'UF Destino', visible: false },
    { id: 'apiDataFreshness', label: 'Atualização', visible: true }
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    itemsPerPage: 25,
    totalItems: 0
  });

  const mockConsultations: ConsultationHistoryItem[] = [
    {
      id: '1',
      consultationId: 'CONS-2024-001',
      companyName: 'Indústria Brasileira de Alimentos Ltda',
      cnpj: '12345678000190',
      createdDate: new Date('2024-01-15T10:30:00'),
      status: 'success',
      productCount: 45,
      totalTaxAmount: 125430.50,
      ufOrigin: 'SP',
      ufDestination: 'RJ',
      cfop: '5102',
      taxRegime: 'lucro_real',
      lastUpdated: new Date('2024-01-15T10:35:00'),
      apiDataFreshness: 'current',
      notes: 'Consulta realizada para operação interestadual com produtos alimentícios',
      createdBy: 'João Silva'
    },
    {
      id: '2',
      consultationId: 'CONS-2024-002',
      companyName: 'Comércio de Eletrônicos São Paulo SA',
      cnpj: '98765432000145',
      createdDate: new Date('2024-01-14T14:20:00'),
      status: 'api_error',
      productCount: 120,
      totalTaxAmount: 0,
      ufOrigin: 'SP',
      ufDestination: 'MG',
      cfop: '6102',
      taxRegime: 'lucro_presumido',
      lastUpdated: new Date('2024-01-14T14:25:00'),
      apiDataFreshness: 'requires_update',
      notes: 'Erro de timeout na API Gestor Tributário - necessário reprocessamento',
      createdBy: 'Maria Santos'
    },
    {
      id: '3',
      consultationId: 'CONS-2024-003',
      companyName: 'Distribuidora Nacional de Bebidas Ltda',
      cnpj: '11223344000156',
      createdDate: new Date('2024-01-13T09:15:00'),
      status: 'success',
      productCount: 78,
      totalTaxAmount: 89750.25,
      ufOrigin: 'RS',
      ufDestination: 'SC',
      cfop: '5405',
      taxRegime: 'simples_nacional',
      lastUpdated: new Date('2024-01-13T09:20:00'),
      apiDataFreshness: 'current',
      notes: 'Operação com bebidas alcoólicas - alíquotas especiais aplicadas',
      createdBy: 'Carlos Oliveira'
    },
    {
      id: '4',
      consultationId: 'CONS-2024-004',
      companyName: 'Farmácia e Drogaria Central Ltda',
      cnpj: '55667788000123',
      createdDate: new Date('2024-01-12T16:45:00'),
      status: 'validation_error',
      productCount: 0,
      totalTaxAmount: 0,
      ufOrigin: 'BA',
      ufDestination: 'PE',
      cfop: '5102',
      taxRegime: 'lucro_presumido',
      lastUpdated: new Date('2024-01-12T16:50:00'),
      apiDataFreshness: 'outdated',
      notes: 'Erro de validação: NCM inválido para 15 produtos',
      createdBy: 'Ana Paula'
    },
    {
      id: '5',
      consultationId: 'CONS-2024-005',
      companyName: 'Construtora e Incorporadora Paulista SA',
      cnpj: '22334455000167',
      createdDate: new Date('2024-01-11T11:00:00'),
      status: 'success',
      productCount: 230,
      totalTaxAmount: 456890.75,
      ufOrigin: 'SP',
      ufDestination: 'SP',
      cfop: '5101',
      taxRegime: 'lucro_real',
      lastUpdated: new Date('2024-01-11T11:15:00'),
      apiDataFreshness: 'current',
      notes: 'Consulta para materiais de construção - operação interna',
      createdBy: 'Roberto Lima'
    },
    {
      id: '6',
      consultationId: 'CONS-2024-006',
      companyName: 'Atacado de Produtos Agrícolas Ltda',
      cnpj: '33445566000178',
      createdDate: new Date('2024-01-10T08:30:00'),
      status: 'processing',
      productCount: 95,
      totalTaxAmount: 0,
      ufOrigin: 'PR',
      ufDestination: 'SC',
      cfop: '6102',
      taxRegime: 'lucro_presumido',
      lastUpdated: new Date('2024-01-10T08:35:00'),
      apiDataFreshness: 'current',
      notes: 'Processamento em andamento - aguardando resposta da API',
      createdBy: 'Fernanda Costa'
    },
    {
      id: '7',
      consultationId: 'CONS-2024-007',
      companyName: 'Indústria Têxtil Nordeste SA',
      cnpj: '44556677000189',
      createdDate: new Date('2024-01-09T13:20:00'),
      status: 'success',
      productCount: 156,
      totalTaxAmount: 234560.80,
      ufOrigin: 'CE',
      ufDestination: 'RN',
      cfop: '6102',
      taxRegime: 'lucro_real',
      lastUpdated: new Date('2024-01-09T13:30:00'),
      apiDataFreshness: 'outdated',
      notes: 'Consulta para produtos têxteis - necessita atualização devido a mudança regulatória',
      createdBy: 'Paulo Mendes'
    },
    {
      id: '8',
      consultationId: 'CONS-2024-008',
      companyName: 'Comércio de Autopeças Brasil Ltda',
      cnpj: '55667788000190',
      createdDate: new Date('2024-01-08T15:10:00'),
      status: 'pending',
      productCount: 0,
      totalTaxAmount: 0,
      ufOrigin: 'MG',
      ufDestination: 'RJ',
      cfop: '5102',
      taxRegime: 'simples_nacional',
      lastUpdated: new Date('2024-01-08T15:15:00'),
      apiDataFreshness: 'requires_update',
      notes: 'Aguardando processamento - fila de consultas',
      createdBy: 'Juliana Rocha'
    }
  ];

  const filteredConsultations = useMemo(() => {
    let result = [...mockConsultations];

    if (filters.companyName) {
      result = result.filter(c =>
        c.companyName.toLowerCase().includes(filters.companyName.toLowerCase()) ||
        c.cnpj.includes(filters.companyName)
      );
    }

    if (filters.status.length > 0) {
      result = result.filter(c => filters.status.includes(c.status));
    }

    if (filters.ufOrigin.length > 0) {
      result = result.filter(c => filters.ufOrigin.includes(c.ufOrigin));
    }

    if (filters.ufDestination.length > 0) {
      result = result.filter(c => filters.ufDestination.includes(c.ufDestination));
    }

    if (filters.taxRegime.length > 0) {
      result = result.filter(c => filters.taxRegime.includes(c.taxRegime));
    }

    if (filters.apiDataFreshness.length > 0) {
      result = result.filter(c => filters.apiDataFreshness.includes(c.apiDataFreshness));
    }

    if (filters.dateRange.start) {
      result = result.filter(c => c.createdDate >= filters.dateRange.start!);
    }

    if (filters.dateRange.end) {
      result = result.filter(c => c.createdDate <= filters.dateRange.end!);
    }

    if (filters.minTaxAmount) {
      result = result.filter(c => c.totalTaxAmount >= parseFloat(filters.minTaxAmount));
    }

    if (filters.maxTaxAmount) {
      result = result.filter(c => c.totalTaxAmount <= parseFloat(filters.maxTaxAmount));
    }

    result.sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [mockConsultations, filters, sortConfig]);

  const paginatedConsultations = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredConsultations.slice(startIndex, endIndex);
  }, [filteredConsultations, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      totalItems: filteredConsultations.length,
      currentPage: 1
    }));
  }, [filteredConsultations.length]);

  const handleSaveFilter = (name: string) => {
    const newFilter: SavedFilter = {
      id: Date.now().toString(),
      name,
      filters: { ...filters },
      createdDate: new Date()
    };
    setSavedFilters([...savedFilters, newFilter]);
  };

  const handleLoadFilter = (filter: SavedFilter) => {
    setFilters(filter.filters);
  };

  const handleDeleteFilter = (id: string) => {
    setSavedFilters(savedFilters.filter(f => f.id !== id));
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: { start: null, end: null },
      status: [],
      companyName: '',
      ufOrigin: [],
      ufDestination: [],
      taxRegime: [],
      apiDataFreshness: [],
      minTaxAmount: '',
      maxTaxAmount: ''
    });
  };

  const handleExport = () => {
    setShowExportDialog(true);
  };

  const handleExportExcel = () => {
    const dataToExport = selectedIds.length > 0
      ? filteredConsultations.filter(c => selectedIds.includes(c.id))
      : filteredConsultations;
    
    exportToExcel(dataToExport, 'consultas-tributarias');
    setShowExportDialog(false);
    
    // Show success notification
    alert(`✅ ${dataToExport.length} consulta${dataToExport.length !== 1 ? 's' : ''} exportada${dataToExport.length !== 1 ? 's' : ''} para Excel com sucesso!`);
  };

  const handleExportCSV = () => {
    const dataToExport = selectedIds.length > 0
      ? filteredConsultations.filter(c => selectedIds.includes(c.id))
      : filteredConsultations;
    
    exportToCSV(dataToExport, 'consultas-tributarias');
    setShowExportDialog(false);
    
    // Show success notification
    alert(`✅ ${dataToExport.length} consulta${dataToExport.length !== 1 ? 's' : ''} exportada${dataToExport.length !== 1 ? 's' : ''} para CSV com sucesso!`);
  };

  const handleReprocess = () => {
    console.log('Reprocessing consultations:', selectedIds);
  };

  const handleUpdateStatus = (status: string) => {
    console.log('Updating status to:', status, 'for:', selectedIds);
  };

  const handleCompare = () => {
    console.log('Comparing consultations:', selectedIds);
  };

  const handleUserMenuClick = () => {
    console.log('User menu clicked');
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <>
      <Helmet>
        <title>Histórico de Consultas - TaxQuery Pro</title>
        <meta
          name="description"
          content="Acesse e gerencie o histórico completo de consultas tributárias com filtros avançados, exportação e análise comparativa"
        />
      </Helmet>

      <div className="flex h-screen bg-background overflow-hidden">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
        />

        <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <Header title="Histórico de Consultas" onUserMenuClick={handleUserMenuClick} />

          <main className="flex-1 flex overflow-hidden mt-16">
            <FilterSidebar
              filters={filters}
              onFilterChange={setFilters}
              savedFilters={savedFilters}
              onSaveFilter={handleSaveFilter}
              onLoadFilter={handleLoadFilter}
              onDeleteFilter={handleDeleteFilter}
              onResetFilters={handleResetFilters}
            />

            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-border bg-card">
                <div className="flex items-center gap-3">
                  <Icon name="History" size={24} color="var(--color-primary)" />
                  <div>
                    <h1 className="text-xl font-semibold text-foreground">
                      Histórico de Consultas Tributárias
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Gerencie e analise todas as consultas realizadas
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleExport}
                  >
                    Exportar
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => window.location.href = '/new-tax-consultation-form'}
                  >
                    Nova Consulta
                  </Button>
                </div>
              </div>

              <ConsultationTable
                consultations={paginatedConsultations}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                sortConfig={sortConfig}
                onSortChange={setSortConfig}
                columnConfig={columnConfig}
                onColumnConfigChange={setColumnConfig}
              />

              <PaginationControls
                pagination={pagination}
                onPageChange={(page) => setPagination({ ...pagination, currentPage: page })}
                onItemsPerPageChange={(itemsPerPage) =>
                  setPagination({ ...pagination, itemsPerPage, currentPage: 1 })
                }
              />
            </div>
          </main>

          <BulkOperationsToolbar
            selectedCount={selectedIds.length}
            onExport={handleExport}
            onReprocess={handleReprocess}
            onUpdateStatus={handleUpdateStatus}
            onCompare={handleCompare}
            onClearSelection={() => setSelectedIds([])}
          />
        </div>
      </div>

      {showExportDialog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowExportDialog(false)}
          />
          <div className="relative bg-card rounded-lg shadow-modal w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Exportar Consultas</h3>
              <button
                onClick={() => setShowExportDialog(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {selectedIds.length > 0
                  ? `Exportar ${selectedIds.length} consulta${selectedIds.length !== 1 ? 's' : ''} selecionada${selectedIds.length !== 1 ? 's' : ''}`
                  : `Exportar todas as ${filteredConsultations.length} consultas filtradas`}
              </p>

              <div className="flex gap-3">
                <Button
                  variant="default"
                  fullWidth
                  iconName="FileSpreadsheet"
                  iconPosition="left"
                  onClick={handleExportExcel}
                >
                  Excel
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  iconName="FileText"
                  iconPosition="left"
                  onClick={handleExportCSV}
                >
                  CSV
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsultationHistory;